import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { 
  Plus, Trash2, UserPlus, Building2, Radio, Loader2, 
  RefreshCw, Search, CheckCircle, XCircle
} from 'lucide-react';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, 
  AlertDialogTitle, AlertDialogTrigger 
} from '../ui/alert-dialog';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminDJAccessTab() {
  const [grants, setGrants] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [granting, setGranting] = useState(false);
  
  // Create Club Dialog
  const [createClubOpen, setCreateClubOpen] = useState(false);
  const [newClub, setNewClub] = useState({ user_email: '', club_name: '' });
  
  // Grant Access Dialog
  const [grantAccessOpen, setGrantAccessOpen] = useState(false);
  const [newGrant, setNewGrant] = useState({ user_email: '', club_id: '' });
  
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Load DJ access grants
      const grantsRes = await fetch(`${API_URL}/api/admin/dj-access-grants`, {
        headers: getAuthHeaders()
      });
      if (grantsRes.ok) {
        const grantsData = await grantsRes.json();
        setGrants(grantsData.grants || []);
      }
      
      // Load clubs for dropdown
      const clubsRes = await fetch(`${API_URL}/api/admin/clubs`, {
        headers: getAuthHeaders()
      });
      if (clubsRes.ok) {
        const clubsData = await clubsRes.json();
        setClubs(Array.isArray(clubsData) ? clubsData : clubsData.clubs || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateClub = async () => {
    if (!newClub.user_email || !newClub.club_name) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/create-club-for-user`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newClub)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message);
        setCreateClubOpen(false);
        setNewClub({ user_email: '', club_name: '' });
        loadData();
      } else {
        toast.error(data.detail || 'Failed to create club');
      }
    } catch (error) {
      toast.error('Failed to create club');
    } finally {
      setCreating(false);
    }
  };

  const handleGrantAccess = async () => {
    if (!newGrant.user_email || !newGrant.club_id) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setGranting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/dj-access-grants`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newGrant)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message);
        setGrantAccessOpen(false);
        setNewGrant({ user_email: '', club_id: '' });
        loadData();
      } else {
        toast.error(data.detail || 'Failed to grant access');
      }
    } catch (error) {
      toast.error('Failed to grant access');
    } finally {
      setGranting(false);
    }
  };

  const handleRevokeAccess = async (grantId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/dj-access-grants/${grantId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('DJ access revoked');
        loadData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to revoke access');
      }
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">DJ Live Access Management</h2>
          <p className="text-slate-600 mt-1">Create clubs for users and manage DJ Live access permissions</p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Create Club for User */}
        <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5 text-violet-600" />
              Create Club for User
            </CardTitle>
            <CardDescription>
              Create a new club and assign it to a user, automatically enabling DJ Live
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setCreateClubOpen(true)} className="w-full bg-violet-600 hover:bg-violet-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Club for User
            </Button>
          </CardContent>
        </Card>

        {/* Grant Standalone Access */}
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Grant DJ Access
            </CardTitle>
            <CardDescription>
              Grant a user DJ Live access to an existing club without making them the owner
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setGrantAccessOpen(true)} className="w-full bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Grant Access to Existing Club
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Active DJ Access Grants */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Radio className="w-5 h-5 text-green-600" />
            Active DJ Access Grants
          </CardTitle>
          <CardDescription>
            Users with standalone DJ Live access (not club owners or instructors)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {grants.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <UserPlus className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No standalone DJ access grants yet</p>
              <p className="text-sm mt-1">Use the buttons above to grant access</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">User</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Club</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Granted</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {grants.map(grant => (
                    <tr key={grant.grant_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900">{grant.user_name}</p>
                          <p className="text-sm text-slate-500">{grant.user_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className="bg-violet-100 text-violet-800">
                          {grant.club_name}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {new Date(grant.granted_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Revoke DJ Access</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to revoke DJ Live access for {grant.user_name} at {grant.club_name}?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleRevokeAccess(grant.grant_id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Revoke Access
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clubs with DJ Live Enabled */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-violet-600" />
            Clubs with DJ Live
          </CardTitle>
          <CardDescription>
            All clubs that have DJ Live enabled
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Club</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Owner</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">DJ Live URL</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {clubs.filter(c => c.dj_live_enabled).map(club => (
                  <tr key={club.club_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{club.name}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {club.owner_email || club.owner_name || 'Unknown'}
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded text-violet-700">
                        /clubs/{club.slug}/live
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Enabled
                      </Badge>
                    </td>
                  </tr>
                ))}
                {clubs.filter(c => c.dj_live_enabled).length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                      No clubs have DJ Live enabled yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Club Dialog */}
      <Dialog open={createClubOpen} onOpenChange={setCreateClubOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-violet-600" />
              Create Club for User
            </DialogTitle>
            <DialogDescription>
              Create a new club and assign it to a user. DJ Live will be automatically enabled.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="user_email">User Email *</Label>
              <Input
                id="user_email"
                type="email"
                value={newClub.user_email}
                onChange={(e) => setNewClub(prev => ({ ...prev, user_email: e.target.value }))}
                placeholder="user@example.com"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">The user must already have an account</p>
            </div>
            
            <div>
              <Label htmlFor="club_name">Club Name *</Label>
              <Input
                id="club_name"
                value={newClub.club_name}
                onChange={(e) => setNewClub(prev => ({ ...prev, club_name: e.target.value }))}
                placeholder="e.g., Learn To Line Dance With Richard"
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateClubOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateClub} 
              disabled={creating}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Club
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Access Dialog */}
      <Dialog open={grantAccessOpen} onOpenChange={setGrantAccessOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Grant DJ Live Access
            </DialogTitle>
            <DialogDescription>
              Grant a user DJ Live access to an existing club without making them the owner.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="grant_user_email">User Email *</Label>
              <Input
                id="grant_user_email"
                type="email"
                value={newGrant.user_email}
                onChange={(e) => setNewGrant(prev => ({ ...prev, user_email: e.target.value }))}
                placeholder="user@example.com"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">The user must already have an account</p>
            </div>
            
            <div>
              <Label htmlFor="grant_club">Club *</Label>
              <Select
                value={newGrant.club_id}
                onValueChange={(value) => setNewGrant(prev => ({ ...prev, club_id: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a club" />
                </SelectTrigger>
                <SelectContent>
                  {clubs.map(club => (
                    <SelectItem key={club.club_id} value={club.club_id}>
                      {club.name}
                      {club.dj_live_enabled && (
                        <span className="ml-2 text-green-600 text-xs">(DJ Live enabled)</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Select the club they should have DJ access to</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantAccessOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGrantAccess} 
              disabled={granting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {granting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Granting...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Grant Access
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminDJAccessTab;
